import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { albumAPI, photoAPI, photoCommentAPI, userAPI, IMG_URL } from '../utils/api';
import { message, Spin, Row, Col, Card, Empty, Image, Result, Button, Space, Input, Avatar, Modal, Tag, Typography } from 'antd';
import { PlusOutlined as PlusIcon, UploadOutlined as UploadIcon, CommentOutlined, MessageOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const AlbumPage = () => {
  const { username } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [isAlbumView, setIsAlbumView] = useState(true);
  const [previewImages, setPreviewImages] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // 照片评论相关
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentPage, setCommentPage] = useState(1);
  const [commentTotal, setCommentTotal] = useState(0);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [commentContent, setCommentContent] = useState('');

  // 获取当前登录用户
  const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
  const isOwner = currentUser?.username === username;

  // 根据当前视图动态设置网页标题
  const pageTitle = isAlbumView
    ? `${username} 的相册`
    : (currentAlbum?.title || '相册');
  useDocumentTitle(pageTitle, {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const albumId = queryParams.get('id');

    if (albumId) {
      setIsAlbumView(false);
      fetchPhotos(albumId);
    } else {
      setIsAlbumView(true);
      fetchAlbums();
    }
  }, [username, location.search]);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      console.log('正在获取相册列表, username:', username);

      // 验证用户是否存在
      const usersRes = await userAPI.getAllUsers();
      const userExists = usersRes.data?.some(u => u.username === username);
      if (!userExists) {
        navigate(`/${username}/error`, {
          state: {
            errorInfo: {
              title: '用户不存在',
              description: `用户 "${username}" 不存在`
            }
          },
          replace: true
        });
        return;
      }

      const response = await albumAPI.getAlbumsByAuthor(username);
      console.log('相册API响应:', response);
      setAlbums(response.data || []);
      console.log('设置的相册数据:', response.data || []);
    } catch (error) {
      console.error('获取相册列表失败:', error);
      navigate(`/${username}/error`, {
        state: {
          errorInfo: {
            title: '数据加载失败',
            description: '无法获取相册列表，请稍后重试'
          }
        },
        replace: true
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async (albumId) => {
    try {
      setLoading(true);
      console.log('正在获取照片列表, albumId:', albumId);

      // 先验证相册是否属于当前用户
      try {
        const albumResponse = await albumAPI.getAlbumByAuthorAndId(username, albumId);
        console.log('相册验证通过:', albumResponse.data);
        setCurrentAlbum(albumResponse.data);
        setAccessDenied(false);
      } catch (error) {
        console.error('相册验证失败:', error);
        navigate(`/${username}/error`, {
          state: {
            errorInfo: {
              title: '相册不存在或无权限',
              description: '相册不存在或您没有权限访问该相册'
            }
          },
          replace: true
        });
        return;
      }

      // 获取照片列表
      const response = await photoAPI.getPhotosByAlbum(albumId);
      console.log('照片API响应:', response);
      const photoData = response.data || [];
      setPhotos(photoData);
      console.log('设置的照片数据:', photoData);

      // 设置预览图片列表
      const previewList = photoData.map(photo => photo.imageUrl ? `${IMG_URL}${photo.imageUrl}` : '/img/default-photo.jpg');
      setPreviewImages(previewList);
    } catch (error) {
      console.error('获取照片列表失败:', error);
      message.error('获取照片列表失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 打开照片评论模态框
  const openCommentModal = async (photo) => {
    setCurrentPhoto(photo);
    setCommentModalVisible(true);
    setCommentPage(1);
    await fetchPhotoComments(photo.id);
  };

  // 获取照片评论
  const fetchPhotoComments = async (photoId, page = 1) => {
    try {
      const response = await photoCommentAPI.getCommentsByPhoto(photoId, page, 10);
      if (response.success) {
        setComments(response.data.list || []);
        setCommentTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('获取评论失败:', error);
    }
  };

  // 提交评论
  const handleSubmitComment = async () => {
    if (!currentUser) {
      message.warning('请先登录后再评论');
      return;
    }

    if (!commentContent.trim()) {
      message.warning('请输入评论内容');
      return;
    }

    try {
      setSubmittingComment(true);
      const commentData = {
        photoId: currentPhoto.id,
        content: commentContent.trim()
      };

      // 如果是回复评论，添加parentId
      if (replyTo) {
        commentData.parentId = replyTo;
      }

      await photoCommentAPI.addComment(commentData);

      message.success('评论发表成功');
      setCommentContent('');
      setReplyTo(null);
      fetchPhotoComments(currentPhoto.id);
      // 更新照片评论数
      setPhotos(photos.map(p =>
        p.id === currentPhoto.id ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p
      ));
      setCurrentPhoto({ ...currentPhoto, commentCount: (currentPhoto.commentCount || 0) + 1 });
    } catch (error) {
      message.error('评论发表失败');
    } finally {
      setSubmittingComment(false);
    }
  };

  // 删除评论
  const handleDeleteComment = async (commentId) => {
    try {
      await photoCommentAPI.deleteComment(commentId);
      message.success('评论删除成功');
      fetchPhotoComments(currentPhoto.id);
      setPhotos(photos.map(p =>
        p.id === currentPhoto.id ? { ...p, commentCount: Math.max(0, (p.commentCount || 0) - 1) } : p
      ));
      setCurrentPhoto({ ...currentPhoto, commentCount: Math.max(0, (currentPhoto.commentCount || 0) - 1) });
    } catch (error) {
      message.error('评论删除失败');
    }
  };

  // 删除照片
  const handleDeletePhoto = async (photoId) => {
    try {
      await photoAPI.deletePhoto(photoId);
      message.success('删除成功');
      fetchPhotos(currentAlbum?.id);
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 渲染评论项（使用后端返回的树形结构）
  const renderCommentItem = (comment) => {
    const canDelete = currentUser && (currentUser.id === comment.userId || currentUser.id === currentPhoto.authorId);
    const user = {
      username: comment.username,
      avatar: comment.avatar
    };

    return (
      <div key={comment.id} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Avatar
            size={40}
            src={user.avatar ? `${IMG_URL}${user.avatar}` : undefined}
            style={{ flexShrink: 0 }}
          >
            {user.username?.[0]?.toUpperCase()}
          </Avatar>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>{user.username || '匿名用户'}</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                {new Date(comment.createTime).toLocaleString('zh-CN')}
              </Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Paragraph style={{ marginBottom: 0 }}>{comment.content}</Paragraph>
            </div>
            <Space>
              <Button
                type="text"
                size="small"
                icon={<MessageOutlined />}
                onClick={() => {
                  setReplyTo(comment.id);
                  setCommentContent(`@${user.username} `);
                }}
              >
                回复
              </Button>
              {canDelete && (
                <Button danger type="text" size="small" onClick={() => handleDeleteComment(comment.id)}>
                  删除
                </Button>
              )}
            </Space>
            {comment.children && comment.children.length > 0 && (
              <div style={{ marginTop: 12, marginLeft: 16 }}>
                {comment.children.map(child => renderCommentItem(child))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
      {accessDenied ? (
        <Result
          status="403"
          title="访问被拒绝"
          subTitle="您没有权限访问该相册"
          extra={<Button type="primary" onClick={() => window.location.href = `/${username}/album/`}>返回相册列表</Button>}
        />
      ) : isAlbumView ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>用户 {username} 的相册</h2>
            {isOwner && (
              <Button type="primary" icon={<PlusIcon />} onClick={() => window.location.href = `/${username}/album/new/`}>
                创建相册
              </Button>
            )}
          </div>
          {albums.length === 0 ? (
            <Empty description="暂无相册" style={{ marginTop: 40 }} />
          ) : (
            <Row gutter={[16, 16]}>
              {albums.map(album => (
                <Col key={album.id} xs={12} sm={8} md={6} lg={4}>
                  <Card
                    hoverable
                    cover={<img alt={album.title} src={album.coverImage ? `${IMG_URL}${album.coverImage}` : '/img/default-album.png'} style={{ width: '100%', height: 180, objectFit: 'cover' }} />}
                    onClick={() => window.location.href = `/${username}/album/?id=${album.id}`}
                    style={{ height: '100%' }}
                    actions={isOwner ? [
                      <Button
                        key="edit"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/${username}/album/edit?id=${album.id}`;
                        }}
                      >
                        编辑
                      </Button>
                    ] : []}
                  >
                    <Card.Meta title={<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{album.title}</div>} description={<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{album.description}</div>} />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>{currentAlbum?.title || '相册'}</h2>
            {isOwner && (
              <Button type="primary" icon={<UploadIcon />} onClick={() => window.location.href = `/${username}/album/upload/?id=${currentAlbum?.id}`}>
                上传图片
              </Button>
            )}
          </div>
          {photos.length === 0 ? (
            <Empty description="暂无照片" style={{ marginTop: 40 }} />
          ) : (
            <Row gutter={[8, 8]}>
              {photos.map((photo, index) => (
                <Col key={photo.id} xs={12} sm={8} md={6} lg={4}>
                    <Card
                      hoverable
                      cover={
                        <Image
                          alt={photo.title}
                          src={photo.imageUrl ? `${IMG_URL}${photo.imageUrl}` : '/default-photo.jpg'}
                          width={'100%'}
                          height={180}
                          style={{ objectFit: 'cover', display: 'block' }}
                          preview={{
                            src: photo.imageUrl ? `${IMG_URL}${photo.imageUrl}` : '/default-photo.jpg',
                          }}
                        />
                      }
                      actions={[
                        <Button
                          key="comment"
                          type="text"
                          icon={<MessageOutlined />}
                          onClick={() => openCommentModal(photo)}
                        >
                          评论 ({photo.commentCount || 0})
                        </Button>,
                        ...(isOwner ? [
                          <Button
                            key="edit"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => window.location.href = `/${username}/album/photo/edit?id=${photo.id}`}
                          >
                            编辑
                          </Button>,
                          <Button
                            key="delete"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeletePhoto(photo.id)}
                          >
                            删除
                          </Button>
                        ] : [])
                      ]}
                    >
                      <Card.Meta
                        title={<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.title}</div>}
                        description={
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                            {photo.description}
                          </div>
                        }
                      />
                    </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      {/* 照片评论模态框 */}
      <Modal
        title={
          <Space>
            <CommentOutlined />
            <span>{currentPhoto?.title || '照片'} - 评论 ({currentPhoto?.commentCount || 0})</span>
          </Space>
        }
        open={commentModalVisible}
        onCancel={() => {
          setCommentModalVisible(false);
          setCurrentPhoto(null);
          setComments([]);
          setReplyTo(null);
          setCommentContent('');
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        {currentPhoto && (
          <div>
            {/* 照片预览 */}
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <img
                src={currentPhoto.imageUrl ? `${IMG_URL}${currentPhoto.imageUrl}` : '/default-photo.jpg'}
                alt={currentPhoto.title}
                style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
              />
            </div>

            {/* 评论输入框 */}
            <div style={{ marginBottom: 16 }}>
              {replyTo && (
                <div style={{ marginBottom: 8 }}>
                  <Tag closable onClose={() => setReplyTo(null)}>
                    回复中...
                  </Tag>
                </div>
              )}
              <Space.Compact style={{ width: '100%', display: 'flex' }}>
                <TextArea
                  rows={3}
                  placeholder={replyTo ? '回复评论...' : '发表评论...'}
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                  style={{ flex: 1, minHeight: 76 }}
                />
                <Button
                  type="primary"
                  loading={submittingComment}
                  onClick={handleSubmitComment}
                  style={{ height: 76, alignSelf: 'flex-start' }}
                >
                  发表
                </Button>
              </Space.Compact>
            </div>

            {/* 评论列表 */}
            {comments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">暂无评论，快来发表第一条评论吧！</Text>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {comments.map(comment => renderCommentItem(comment))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AlbumPage;